import React, { useState, useEffect } from 'react';
import { Shield, Lock, CreditCard, HelpCircle } from 'lucide-react';
import Header from '../../components/navigation/Header';
import Icon from '../../components/AppIcon';
import ServiceSelectionDashboard from './components/ServiceSelectionDashboard';
import PasswordResetWorkflow from './components/PasswordResetWorkflow';
import AccountRecoveryWorkflow from './components/AccountRecoveryWorkflow';
import PaymentVerificationPanel from './components/PaymentVerificationPanel';

const ClientSelfServiceAccountRecoveryPortal = () => {
  const [activeService, setActiveService] = useState('password-reset');

  // Check URL parameters for initial service
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params?.get('tab');
    if (tab && ['password-reset', 'account-recovery', 'payment-verification']?.includes(tab)) {
      setActiveService(tab);
    }
  }, []);

  const renderServicePanel = () => {
    switch (activeService) {
      case 'password-reset':
        return <PasswordResetWorkflow />;
      case 'account-recovery':
        return <AccountRecoveryWorkflow />;
      case 'payment-verification':
        return <PaymentVerificationPanel />;
      default:
        return <PasswordResetWorkflow />;
    }
  };

  const getServiceTitle = () => {
    switch (activeService) {
      case 'password-reset':
        return 'Password Reset';
      case 'account-recovery':
        return 'Account Recovery';
      case 'payment-verification':
        return 'Payment Verification';
      default:
        return 'Self-Service Portal';
    }
  };

  const getServiceIcon = () => {
    switch (activeService) {
      case 'password-reset':
        return Lock;
      case 'account-recovery':
        return Shield;
      case 'payment-verification':
        return CreditCard;
      default:
        return Shield;
    }
  };

  const ServiceIcon = getServiceIcon();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center">
              <Shield size={24} className="text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Client Self-Service Portal
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Manage your account independently without admin intervention
              </p>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Service Selection Sidebar */}
          <div className="lg:col-span-4">
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
              <ServiceSelectionDashboard
                activeService={activeService}
                onServiceSelect={setActiveService}
              />
            </div>

            {/* Help Section */}
            <div className="mt-6 bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
              <div className="flex items-center gap-2 mb-3">
                <HelpCircle size={20} className="text-emerald-600 dark:text-emerald-400" />
                <h3 className="font-semibold text-foreground">Need Help?</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                If you're unable to resolve your issue using self-service options, our support team is here to help.
              </p>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Icon name="Mail" size={16} className="text-emerald-600 dark:text-emerald-400" />
                  <span>support@pnghranalytics.com</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Icon name="Phone" size={16} className="text-emerald-600 dark:text-emerald-400" />
                  <span>+675 XXX XXXX</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Icon name="Clock" size={16} className="text-emerald-600 dark:text-emerald-400" />
                  <span>Mon-Fri: 8AM - 5PM AEST</span>
                </div>
              </div>
            </div>
          </div>

          {/* Main Service Panel */}
          <div className="lg:col-span-8">
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
              {/* Service Header */}
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200 dark:border-slate-700">
                <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center">
                  <ServiceIcon size={20} className="text-emerald-600 dark:text-emerald-400" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-foreground">{getServiceTitle()}</h2>
                  <p className="text-sm text-muted-foreground">Complete the steps below to proceed</p>
                </div>
              </div>

              {/* Service Content */}
              {renderServicePanel()}
            </div>

            {/* Security Notice */}
            <div className="mt-6 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon name="Shield" size={20} className="text-amber-600 dark:text-amber-400 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-medium text-amber-900 dark:text-amber-100 mb-1">Security & Privacy</h4>
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    All self-service operations are logged for security purposes. Suspicious activity is monitored and may result in temporary account suspension. Your data is protected with bank-grade encryption.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientSelfServiceAccountRecoveryPortal;